import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon } from "@phosphor-icons/react";

export default function AdminLogin() {
    const { admin, login, ready } = useAdminAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (ready && admin && typeof admin === "object") {
            navigate("/admin");
        }
    }, [admin, ready, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const res = await login(email.trim(), password);
        setLoading(false);
        if (res.ok) {
            navigate("/admin");
        } else {
            setErr(res.error);
        }
    };

    return (
        <div className="min-h-screen flex bg-bg text-white">
            <Seo title="Admin Login — India Reels" description="India Reels admin panel" path="/admin/login" />

            {/* Visual side */}
            <div className="hidden md:flex flex-1 relative items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(224,26,79,0.3),transparent_60%),radial-gradient(circle_at_70%_70%,rgba(255,167,0,0.2),transparent_60%)]" />
                <div className="relative z-10 text-center px-10 max-w-md">
                    <Link to="/" className="inline-block mb-10">
                        <Logo size={48} textSize="text-3xl" />
                    </Link>
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary/15 flex items-center justify-center">
                        <ShieldCheckIcon size={42} weight="duotone" color="#E01A4F" />
                    </div>
                    <h2 className="font-heading font-black text-3xl leading-tight">
                        Admin <span className="gradient-text">control panel</span>
                    </h2>
                    <p className="text-white/60 mt-3">
                        Manage support tickets, contact messages and account deletion requests.
                    </p>
                </div>
            </div>

            {/* Form side */}
            <div className="flex-1 flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-md fade-up">
                    <Link to="/" className="md:hidden inline-block mb-10">
                        <Logo size={40} textSize="text-2xl" />
                    </Link>
                    <h1 className="font-heading font-black text-3xl md:text-4xl">Admin sign-in</h1>
                    <p className="text-text-secondary mt-2">
                        Use your admin credentials. Don&apos;t have access?{" "}
                        <Link to="/contact" className="text-primary hover:underline font-semibold">
                            Contact support
                        </Link>
                        .
                    </p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                                Email
                            </label>
                            <input
                                data-testid="admin-email-input"
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@india-reels.com"
                                className="input-field mt-2"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                                Password
                            </label>
                            <div className="relative mt-2">
                                <input
                                    data-testid="admin-password-input"
                                    type={showPw ? "text" : "password"}
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="input-field pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-white"
                                >
                                    {showPw ? <EyeSlashIcon size={20} /> : <EyeIcon size={20} />}
                                </button>
                            </div>
                        </div>

                        {err && (
                            <div
                                data-testid="admin-login-error"
                                className="text-primary text-sm bg-primary/10 px-4 py-3 rounded-xl"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="btn-primary w-full py-3.5 disabled:opacity-60"
                        >
                            {loading ? "Signing in..." : "Sign in to dashboard"}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <Link to="/" className="text-text-secondary text-sm hover:text-white">
                            ← Back to website
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
